"use client";

import React, { useState, useMemo, useCallback } from "react";
import { CodeHighlight } from "@/components/CodeHighlight";
import {
  Globe2,
  AlertTriangle,
  CheckCircle2,
  Copy,
  Check,
  Download,
  Sparkles,
  Calendar,
  Terminal,
  ShieldAlert,
} from "lucide-react";

type ExportTarget = "crontab" | "github" | "systemd";

const MONTH_ALIASES: Record<string, number> = {
  JAN: 1, FEB: 2, MAR: 3, APR: 4, MAY: 5, JUN: 6,
  JUL: 7, AUG: 8, SEP: 9, OCT: 10, NOV: 11, DEC: 12,
};

const DOW_ALIASES: Record<string, number> = {
  SUN: 0, MON: 1, TUE: 2, WED: 3, THU: 4, FRI: 5, SAT: 6,
};

const DOW_NAMES_DE = [
  "Sonntag",
  "Montag",
  "Dienstag",
  "Mittwoch",
  "Donnerstag",
  "Freitag",
  "Samstag",
];

const MONTH_NAMES_DE = [
  "",
  "Januar",
  "Februar",
  "März",
  "April",
  "Mai",
  "Juni",
  "Juli",
  "August",
  "September",
  "Oktober",
  "November",
  "Dezember",
];

const MACROS: Record<string, string> = {
  "@hourly": "0 * * * *",
  "@daily": "0 0 * * *",
  "@midnight": "0 0 * * *",
  "@weekly": "0 0 * * 0",
  "@monthly": "0 0 1 * *",
  "@yearly": "0 0 1 1 *",
  "@annually": "0 0 1 1 *",
};

const TIMEZONES = [
  { id: "Europe/Berlin", label: "Europe/Berlin (MEZ / MESZ)", hasDst: true },
  { id: "UTC", label: "UTC (Weltzeit · GitHub Actions Standard)", hasDst: false },
  { id: "Europe/London", label: "Europe/London (GMT / BST)", hasDst: true },
  { id: "America/New_York", label: "America/New_York (EST / EDT)", hasDst: true },
  { id: "Asia/Tokyo", label: "Asia/Tokyo (JST · keine Sommerzeit)", hasDst: false },
];

const PRESETS = [
  { label: "Werktags 08:15", expr: "15 8 * * 1-5" },
  { label: "Alle 15 Min. (Bürozeit)", expr: "*/15 9-17 * * 1-5" },
  { label: "02:30 Nacht-Backup (DST-Falle!)", expr: "30 2 * * *" },
  { label: "Freitag der 13.? (ODER-Falle!)", expr: "0 9 13 * 5" },
  { label: "1. im Monat 04:00", expr: "0 4 1 * *" },
  { label: "Sonntags 03:20", expr: "20 3 * * 0" },
];

const FIELD_META = [
  {
    idx: 0,
    title: "Minute",
    range: "0–59",
    chips: [
      { label: "Jede (*)", val: "*" },
      { label: ":00", val: "0" },
      { label: "Alle 5 (*/5)", val: "*/5" },
      { label: "Alle 15 (*/15)", val: "*/15" },
    ],
  },
  {
    idx: 1,
    title: "Stunde",
    range: "0–23",
    chips: [
      { label: "Jede (*)", val: "*" },
      { label: "03 Uhr", val: "3" },
      { label: "08 Uhr", val: "8" },
      { label: "Büro (9-17)", val: "9-17" },
    ],
  },
  {
    idx: 2,
    title: "Tag (Monat)",
    range: "1–31",
    chips: [
      { label: "Jeder (*)", val: "*" },
      { label: "1. Tag", val: "1" },
      { label: "15. Tag", val: "15" },
    ],
  },
  {
    idx: 3,
    title: "Monat",
    range: "1–12",
    chips: [
      { label: "Jeder (*)", val: "*" },
      { label: "Quartal (*/3)", val: "*/3" },
      { label: "Jan (1)", val: "1" },
    ],
  },
  {
    idx: 4,
    title: "Wochentag",
    range: "0–6 (So–Sa)",
    chips: [
      { label: "Jeder (*)", val: "*" },
      { label: "Mo–Fr (1-5)", val: "1-5" },
      { label: "So (0)", val: "0" },
      { label: "Sa,So (6,0)", val: "6,0" },
    ],
  },
];

interface ParsedCron {
  rawFields: [string, string, string, string, string];
  minutes: Set<number>;
  hours: Set<number>;
  daysOfMonth: Set<number>;
  months: Set<number>;
  daysOfWeek: Set<number>;
  domWildcard: boolean;
  dowWildcard: boolean;
}

function replaceAliases(token: string, map: Record<string, number>): string {
  return token.toUpperCase().replace(/[A-Z]{3}/g, (m) =>
    map[m] !== undefined ? String(map[m]) : m
  );
}

function parseCronField(
  raw: string,
  min: number,
  max: number,
  fieldLabel: string,
  isDow = false
): Set<number> {
  const cleaned = isDow
    ? replaceAliases(raw.trim(), DOW_ALIASES)
    : min === 1 && max === 12
      ? replaceAliases(raw.trim(), MONTH_ALIASES)
      : raw.trim();

  if (!cleaned) {
    throw new Error(`Feld „${fieldLabel}“ ist leer.`);
  }

  const values = new Set<number>();
  const parts = cleaned.split(",");

  for (const part of parts) {
    const [rangePart, stepPart] = part.split("/");
    if (part.split("/").length > 2) {
      throw new Error(`Ungültiger Schrittwert „${part}“ im Feld „${fieldLabel}“.`);
    }

    const step = stepPart !== undefined ? Number(stepPart) : 1;
    if (!Number.isInteger(step) || step <= 0) {
      throw new Error(`Schrittweite „${stepPart}“ in „${fieldLabel}“ muss eine positive Zahl sein.`);
    }

    let start = min;
    let end = max;

    if (rangePart === "*") {
      start = min;
      end = max;
    } else if (rangePart.includes("-")) {
      const [sStr, eStr] = rangePart.split("-");
      start = Number(sStr);
      end = Number(eStr);
      if (isDow && end === 7) end = 7;
      if (
        !Number.isInteger(start) ||
        !Number.isInteger(end) ||
        start < min ||
        end > (isDow ? 7 : max) ||
        start > end
      ) {
        throw new Error(
          `Bereich „${rangePart}“ in „${fieldLabel}“ liegt außerhalb von ${min}–${max}.`
        );
      }
    } else {
      const single = Number(rangePart);
      const effectiveMax = isDow ? 7 : max;
      if (!Number.isInteger(single) || single < min || single > effectiveMax) {
        throw new Error(
          `Wert „${rangePart}“ in „${fieldLabel}“ muss zwischen ${min} und ${max} liegen.`
        );
      }
      start = single;
      end = stepPart !== undefined ? max : single;
    }

    for (let v = start; v <= end; v += step) {
      values.add(isDow && v === 7 ? 0 : v);
    }
  }

  return values;
}

function parseCronExpression(input: string): { cron: ParsedCron | null; error: string | null } {
  const trimmed = input.trim();
  if (!trimmed) return { cron: null, error: "Bitte gib einen Cron-Ausdruck mit 5 Feldern ein." };

  const expanded = MACROS[trimmed.toLowerCase()] ?? trimmed;
  const tokens = expanded.split(/\s+/);
  if (tokens.length !== 5) {
    return {
      cron: null,
      error: `Klassischer POSIX-Cron erwartet genau 5 Felder (gefunden: ${tokens.length}). Sekunden-Felder (6 Stellen) werden von Standard-Crontab und GitHub Actions nicht unterstützt.`,
    };
  }

  try {
    const [mRaw, hRaw, domRaw, monRaw, dowRaw] = tokens as [
      string,
      string,
      string,
      string,
      string,
    ];
    const minutes = parseCronField(mRaw, 0, 59, "Minute");
    const hours = parseCronField(hRaw, 0, 23, "Stunde");
    const daysOfMonth = parseCronField(domRaw, 1, 31, "Tag im Monat");
    const months = parseCronField(monRaw, 1, 12, "Monat");
    const daysOfWeek = parseCronField(dowRaw, 0, 6, "Wochentag", true);

    return {
      cron: {
        rawFields: [mRaw, hRaw, domRaw, monRaw, dowRaw],
        minutes,
        hours,
        daysOfMonth,
        months,
        daysOfWeek,
        domWildcard: domRaw.trim() === "*",
        dowWildcard: dowRaw.trim() === "*",
      },
      error: null,
    };
  } catch (err) {
    return {
      cron: null,
      error: err instanceof Error ? err.message : "Ungültiger Cron-Ausdruck.",
    };
  }
}

function describeCronGerman(cron: ParsedCron): string {
  const [mRaw, hRaw, domRaw, monRaw, dowRaw] = cron.rawFields;

  // Time part
  let timeDesc = "";
  if (mRaw === "*" && hRaw === "*") {
    timeDesc = "Jede einzelne Minute";
  } else if (mRaw.startsWith("*/") && hRaw === "*") {
    timeDesc = `Alle ${mRaw.slice(2)} Minuten`;
  } else if (mRaw.startsWith("*/") && hRaw !== "*") {
    timeDesc = `Alle ${mRaw.slice(2)} Minuten während Stunde ${hRaw}`;
  } else if (cron.minutes.size === 1 && cron.hours.size === 1) {
    const mm = String(Array.from(cron.minutes)[0]).padStart(2, "0");
    const hh = String(Array.from(cron.hours)[0]).padStart(2, "0");
    timeDesc = `Um ${hh}:${mm} Uhr`;
  } else if (cron.minutes.size === 1 && hRaw === "*") {
    const mm = String(Array.from(cron.minutes)[0]).padStart(2, "0");
    timeDesc = `Jede Stunde zur Minute :${mm}`;
  } else {
    const mins = Array.from(cron.minutes)
      .sort((a, b) => a - b)
      .map((n) => String(n).padStart(2, "0"))
      .join(", ");
    const hrs =
      hRaw === "*"
        ? "jeder Stunde"
        : `Stunde ${Array.from(cron.hours)
            .sort((a, b) => a - b)
            .join(", ")}`;
    timeDesc = `Bei Minute ${mins} in ${hrs}`;
  }

  // Day part
  let dayDesc = "";
  const formatDowSet = () => {
    if (dowRaw === "1-5") return "montags bis freitags";
    if (dowRaw === "6,0" || dowRaw === "0,6") return "am Wochenende (Sa & So)";
    return (
      "jeden " +
      Array.from(cron.daysOfWeek)
        .sort((a, b) => a - b)
        .map((d) => DOW_NAMES_DE[d])
        .join(", ")
    );
  };

  if (cron.domWildcard && cron.dowWildcard) {
    dayDesc = "jeden Tag";
  } else if (!cron.domWildcard && cron.dowWildcard) {
    dayDesc = `am ${domRaw}. Tag des Monats`;
  } else if (cron.domWildcard && !cron.dowWildcard) {
    dayDesc = formatDowSet();
  } else {
    dayDesc = `am ${domRaw}. Tag des Monats ODER ${formatDowSet()} (POSIX-ODER-Regel!)`;
  }

  // Month part
  let monDesc = "";
  if (monRaw !== "*") {
    if (monRaw.startsWith("*/")) {
      monDesc = `, alle ${monRaw.slice(2)} Monate`;
    } else {
      const mList = Array.from(cron.months)
        .sort((a, b) => a - b)
        .map((m) => MONTH_NAMES_DE[m])
        .join(", ");
      monDesc = ` im ${mList}`;
    }
  }

  return `${timeDesc}, ${dayDesc}${monDesc}.`;
}

const WEEKDAY_EN_MAP: Record<string, number> = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
};

interface ExecutionPreview {
  epochMs: number;
  localFormatted: string;
  utcFormatted: string;
  relativeText: string;
  duplicateLocalWallClock: boolean;
}

function computeNextExecutions(
  cron: ParsedCron,
  timeZone: string,
  baseEpochMs: number,
  count = 10
): ExecutionPreview[] {
  const partFmt = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hourCycle: "h23",
    year: "numeric",
    month: "numeric",
    day: "numeric",
    weekday: "short",
    hour: "numeric",
    minute: "numeric",
  });

  const displayLocalFmt = new Intl.DateTimeFormat("de-DE", {
    timeZone,
    weekday: "short",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZoneName: "short",
  });

  const displayUtcFmt = new Intl.DateTimeFormat("de-DE", {
    timeZone: "UTC",
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });

  const extractWall = (ms: number) => {
    const parts = partFmt.formatToParts(new Date(ms));
    let month = 1;
    let day = 1;
    let hour = 0;
    let minute = 0;
    let dow = 0;
    let year = 2026;
    for (const p of parts) {
      if (p.type === "year") year = Number(p.value);
      else if (p.type === "month") month = Number(p.value);
      else if (p.type === "day") day = Number(p.value);
      else if (p.type === "hour") hour = Number(p.value) % 24;
      else if (p.type === "minute") minute = Number(p.value);
      else if (p.type === "weekday") dow = WEEKDAY_EN_MAP[p.value] ?? 0;
    }
    return { year, month, day, hour, minute, dow };
  };

  const results: ExecutionPreview[] = [];
  const seenWallKeys = new Set<string>();

  // Start at the next full minute after baseEpochMs
  let cursor = Math.floor(baseEpochMs / 60_000) * 60_000 + 60_000;
  let guard = 0;

  while (results.length < count && guard < 24_000) {
    guard++;
    const w = extractWall(cursor);

    const monthOk = cron.months.has(w.month);
    const dayOk =
      cron.domWildcard && cron.dowWildcard
        ? true
        : !cron.domWildcard && cron.dowWildcard
          ? cron.daysOfMonth.has(w.day)
          : cron.domWildcard && !cron.dowWildcard
            ? cron.daysOfWeek.has(w.dow)
            : cron.daysOfMonth.has(w.day) || cron.daysOfWeek.has(w.dow);

    if (!monthOk || !dayOk || !cron.hours.has(w.hour)) {
      // Jump to :00 of the next hour (DST-safe across integer-hour zones)
      const jumpMinutes = Math.max(1, 60 - w.minute);
      cursor += jumpMinutes * 60_000;
      continue;
    }

    if (cron.minutes.has(w.minute)) {
      const wallKey = `${w.year}-${w.month}-${w.day}-${w.hour}-${w.minute}`;
      const isDup = seenWallKeys.has(wallKey);
      seenWallKeys.add(wallKey);

      const diffMin = Math.round((cursor - baseEpochMs) / 60_000);
      const relativeText =
        diffMin < 60
          ? `in ${diffMin} Min.`
          : diffMin < 1440
            ? `in ${Math.floor(diffMin / 60)} Std. ${diffMin % 60} Min.`
            : `in ${Math.floor(diffMin / 1440)} Tg. ${Math.floor((diffMin % 1440) / 60)} Std.`;

      results.push({
        epochMs: cursor,
        localFormatted: displayLocalFmt.format(new Date(cursor)),
        utcFormatted: `${displayUtcFmt.format(new Date(cursor))} UTC`,
        relativeText,
        duplicateLocalWallClock: isDup,
      });
    }

    cursor += 60_000;
  }

  return results;
}

function buildExportSnippet(
  expr: string,
  cron: ParsedCron,
  target: ExportTarget,
  timeZone: string
): { code: string; lang: string } {
  const cleanExpr = MACROS[expr.trim().toLowerCase()] ?? expr.trim();

  if (target === "crontab") {
    return {
      lang: "bash",
      code: `# /etc/crontab oder crontab -e (Zeitzone prüfen: CRON_TZ=${timeZone})
CRON_TZ=${timeZone}

# flock verhindert überlappende Prozesse, falls der vorherige Lauf noch hängt:
${cleanExpr} /usr/bin/flock -n /tmp/mein-job.lock /opt/scripts/backup.sh >> /var/log/backup.log 2>&1`,
    };
  }

  if (target === "github") {
    return {
      lang: "yaml",
      code: `# ACHTUNG: GitHub Actions wertet schedule IMMER in UTC aus (keine Sommerzeit)!
name: Geplanter Wartungslauf

on:
  schedule:
    # Geprüfter Ausdruck (${timeZone === "UTC" ? "bereits in UTC" : `hier ggf. UTC-Versatz zu ${timeZone} einplanen!`}):
    - cron: '${cleanExpr}'
  workflow_dispatch: # Erlaubt jederzeit manuellen Test-Start

concurrency:
  group: scheduled-maintenance
  cancel-in-progress: false

jobs:
  run-task:
    runs-on: ubuntu-latest
    steps:
      - uses: actions/checkout@v4
      - run: npm run maintenance`,
    };
  }

  // systemd timer
  const [mRaw, hRaw, domRaw, monRaw, dowRaw] = cron.rawFields;
  const dowPrefix = dowRaw === "*" ? "" : dowRaw === "1-5" ? "Mon..Fri " : `${dowRaw} `;
  const datePart = `${monRaw === "*" ? "*" : monRaw}-${domRaw === "*" ? "*" : domRaw}`;
  const timePart = `${hRaw === "*" ? "*" : hRaw.padStart(2, "0")}:${mRaw === "*" ? "*" : mRaw.padStart(2, "0")}:00`;

  return {
    lang: "ini",
    code: `# /etc/systemd/system/mein-job.timer
[Unit]
Description=Geplanter Dienst (${cleanExpr})

[Timer]
OnCalendar=${dowPrefix}*-${datePart} ${timePart}
# Holt verpasste Läufe nach einem Server-Reboot sofort nach (Vorteil gegenüber Cron!):
Persistent=true
RandomizedDelaySec=30s

[Install]
WantedBy=timers.target`,
  };
}

export default function CronPlannerPlayground() {
  const [expression, setExpression] = useState<string>("15 8 * * 1-5");
  const [timeZone, setTimeZone] = useState<string>("Europe/Berlin");
  // Fixed deterministic initial reference anchor (2026-10-27T00:00:00Z) so SSR & Client match cleanly, plus 1-click live/DST anchors
  const [anchorMode, setAnchorMode] = useState<"now" | "spring-dst" | "autumn-dst">("now");
  const [liveNowMs, setLiveNowMs] = useState<number>(() => Date.UTC(2026, 9, 27, 7, 0, 0));
  const [exportTarget, setExportTarget] = useState<ExportTarget>("crontab");
  const [copiedExport, setCopiedExport] = useState(false);

  const baseEpochMs = useMemo(() => {
    if (anchorMode === "spring-dst") {
      // Last Sunday of March 2027 in Europe: 2027-03-28 00:30 UTC (01:30 CET -> 02:00 jumps to 03:00 CEST!)
      return Date.UTC(2027, 2, 28, 0, 15, 0);
    }
    if (anchorMode === "autumn-dst") {
      // Last Sunday of October 2027 in Europe: 2027-10-31 00:15 UTC (02:15 CEST -> 03:00 jumps back to 02:00 CET!)
      return Date.UTC(2027, 9, 31, 0, 15, 0);
    }
    return liveNowMs;
  }, [anchorMode, liveNowMs]);

  const parsed = useMemo(() => parseCronExpression(expression), [expression]);
  const tzInfo = TIMEZONES.find((t) => t.id === timeZone) ?? TIMEZONES[0];

  const humanSummary = useMemo(
    () => (parsed.cron ? describeCronGerman(parsed.cron) : null),
    [parsed.cron]
  );

  const executions = useMemo(() => {
    if (!parsed.cron) return [];
    return computeNextExecutions(parsed.cron, timeZone, baseEpochMs, 10);
  }, [parsed.cron, timeZone, baseEpochMs]);

  const hasPosixOrTrap = Boolean(
    parsed.cron && !parsed.cron.domWildcard && !parsed.cron.dowWildcard
  );

  const hasDstRisk = Boolean(
    parsed.cron &&
      tzInfo.hasDst &&
      parsed.cron.rawFields[1] !== "*" &&
      (parsed.cron.hours.has(2) ||
        (timeZone === "Europe/London" && parsed.cron.hours.has(1)))
  );

  const exportData = useMemo(() => {
    if (!parsed.cron) return null;
    return buildExportSnippet(expression, parsed.cron, exportTarget, timeZone);
  }, [expression, parsed.cron, exportTarget, timeZone]);

  const updateSingleField = useCallback(
    (fieldIdx: number, nextVal: string) => {
      const base = (MACROS[expression.trim().toLowerCase()] ?? expression.trim()).split(/\s+/);
      while (base.length < 5) base.push("*");
      base[fieldIdx] = nextVal;
      setExpression(base.slice(0, 5).join(" "));
    },
    [expression]
  );

  const downloadIcs = () => {
    if (executions.length === 0) return;
    const fmtIcsUtc = (ms: number) =>
      new Date(ms).toISOString().replace(/[-:]/g, "").replace(/\.\d{3}Z$/, "Z");

    const events = executions.map((ex, idx) => {
      const start = fmtIcsUtc(ex.epochMs);
      const end = fmtIcsUtc(ex.epochMs + 15 * 60_000);
      return [
        "BEGIN:VEVENT",
        `UID:cron-${idx}-${ex.epochMs}@lukiluki.me`,
        `DTSTAMP:${fmtIcsUtc(Date.now())}`,
        `DTSTART:${start}`,
        `DTEND:${end}`,
        `SUMMARY:Cron-Lauf (${expression.trim()})`,
        `DESCRIPTION:${humanSummary ?? ""}`,
        "END:VEVENT",
      ].join("\r\n");
    });

    const ics = [
      "BEGIN:VCALENDAR",
      "VERSION:2.0",
      "PRODID:-//lukiluki.me//Cron-Pruefstand//DE",
      ...events,
      "END:VCALENDAR",
    ].join("\r\n");

    const blob = new Blob([ics], { type: "text/calendar;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "cron-zeitplan.ics";
    a.click();
    URL.revokeObjectURL(url);
  };

  const currentFields = (MACROS[expression.trim().toLowerCase()] ?? expression.trim()).split(/\s+/);

  return (
    <div className="flex flex-col gap-5">
      {/* Presets Row */}
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="mr-1 text-xs font-semibold text-neutral-500 dark:text-neutral-400">
          Typische Zeitpläne &amp; Fallen:
        </span>
        {PRESETS.map((p) => (
          <button
            key={p.expr}
            type="button"
            onClick={() => {
              setExpression(p.expr);
              if (p.expr === "30 2 * * *") setAnchorMode("spring-dst");
            }}
            className={`inline-flex min-h-[38px] items-center gap-1.5 rounded-xl border px-3 py-1.5 text-xs font-semibold transition-colors cursor-pointer ${
              expression.trim() === p.expr
                ? "border-sky-500 bg-sky-600 text-white"
                : "border-neutral-200/90 bg-white text-neutral-700 hover:bg-neutral-100 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-200"
            }`}
          >
            <Sparkles className="h-3 w-3 opacity-80" />
            <span>{p.label}</span>
          </button>
        ))}
      </div>

      {/* Main Expression Hero Box + German Translation */}
      <div className="rounded-2xl border border-sky-500/30 bg-sky-500/[0.05] p-4 sm:p-5 dark:border-sky-500/20 dark:bg-sky-500/[0.04]">
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-12 lg:items-center">
          <div className="lg:col-span-5">
            <label
              htmlFor="cron-expr-input"
              className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400"
            >
              5-Felder Cron-Ausdruck
            </label>
            <input
              id="cron-expr-input"
              type="text"
              value={expression}
              onChange={(e) => setExpression(e.target.value)}
              spellCheck={false}
              className="min-h-[52px] w-full rounded-xl border-2 border-neutral-300 bg-white px-4 py-2.5 font-mono text-xl sm:text-2xl font-black tracking-widest text-neutral-900 focus:border-sky-500 focus:outline-hidden dark:border-neutral-700 dark:bg-neutral-950 dark:text-white"
            />
          </div>

          <div className="lg:col-span-7">
            <div className="text-xs font-bold uppercase tracking-wider text-sky-700 dark:text-sky-400">
              Klartext-Bedeutung
            </div>
            {humanSummary ? (
              <p className="mt-1 font-heading text-lg sm:text-xl font-bold text-neutral-900 dark:text-white">
                „{humanSummary}“
              </p>
            ) : (
              <div className="mt-1.5 flex items-start gap-2 rounded-xl border border-red-500/40 bg-red-500/10 p-3 text-xs font-medium text-red-800 dark:text-red-200">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{parsed.error}</span>
              </div>
            )}
          </div>
        </div>

        {/* 5 Visual Field Builder Cards */}
        <div className="mt-4 grid grid-cols-2 gap-2.5 sm:grid-cols-5">
          {FIELD_META.map((fm) => {
            const val = currentFields[fm.idx] ?? "*";
            return (
              <div
                key={fm.title}
                className="flex flex-col justify-between rounded-xl border border-neutral-200/90 bg-white p-2.5 dark:border-neutral-800 dark:bg-neutral-900"
              >
                <div>
                  <div className="flex items-center justify-between text-[11px]">
                    <span className="font-bold text-neutral-800 dark:text-neutral-200">
                      {fm.title}
                    </span>
                    <span className="font-mono text-[10px] text-neutral-400">{fm.range}</span>
                  </div>
                  <input
                    type="text"
                    aria-label={fm.title}
                    value={val}
                    onChange={(e) => updateSingleField(fm.idx, e.target.value || "*")}
                    className="mt-1.5 min-h-[34px] w-full rounded-lg border border-neutral-200 bg-neutral-50 px-2 py-1 text-center font-mono text-xs font-bold text-neutral-900 focus:border-sky-500 focus:outline-hidden dark:border-neutral-700 dark:bg-neutral-950 dark:text-white"
                  />
                </div>
                <div className="mt-2 flex flex-wrap gap-1">
                  {fm.chips.map((c) => (
                    <button
                      key={c.val}
                      type="button"
                      onClick={() => updateSingleField(fm.idx, c.val)}
                      className={`rounded px-1.5 py-0.5 font-mono text-[10px] font-semibold transition-colors cursor-pointer ${
                        val === c.val
                          ? "bg-sky-600 text-white"
                          : "bg-neutral-100 text-neutral-600 hover:bg-neutral-200 dark:bg-neutral-800 dark:text-neutral-300"
                      }`}
                    >
                      {c.label}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Active Engineering Guardrail Warnings */}
      {(hasPosixOrTrap || hasDstRisk) && (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {hasPosixOrTrap && (
            <div className="flex items-start gap-2.5 rounded-2xl border border-amber-500/40 bg-amber-500/10 p-3.5 text-xs text-amber-950 dark:text-amber-200">
              <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
              <div>
                <p className="font-bold">Berüchtigte POSIX-ODER-Falle erkannt!</p>
                <p className="mt-0.5 leading-relaxed">
                  Sowohl <strong>Tag im Monat</strong> als auch <strong>Wochentag</strong> sind gesetzt.
                  Laut POSIX-Standard werden diese beiden Felder per <strong>ODER</strong> statt UND verknüpft:
                  <code className="mx-1 font-mono">0 9 13 * 5</code> läuft also an <em>jedem</em> Freitag{" "}
                  <strong>und</strong> an <em>jedem</em> 13. des Monats!
                </p>
              </div>
            </div>
          )}

          {hasDstRisk && (
            <div className="flex items-start gap-2.5 rounded-2xl border border-amber-500/40 bg-amber-500/10 p-3.5 text-xs text-amber-950 dark:text-amber-200">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
              <div>
                <p className="font-bold">Zeitumstellungs-Risiko in {timeZone}!</p>
                <p className="mt-0.5 leading-relaxed">
                  Die Stunde <strong>02:00–02:59 Uhr</strong> existiert am März-Umstellungstag nicht
                  (Job fällt aus!) und läuft am Oktober-Umstellungstag zweimal hintereinander.
                  Wähle für nächtliche Backups lieber <strong>01:xx</strong>, <strong>03:xx</strong> oder{" "}
                  <strong>UTC</strong>.
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Bottom 2-Column Split: Next 10 Executions (Timezone Simulator) + Config Generator */}
      {parsed.cron && (
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
          {/* Left: Timezone & Next 10 Runs */}
          <div className="flex flex-col gap-3 rounded-2xl border border-neutral-200/90 bg-white p-4 lg:col-span-7 dark:border-neutral-800 dark:bg-neutral-900">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Globe2 className="h-4 w-4 text-sky-500" />
                <span className="font-heading text-sm font-bold text-neutral-900 dark:text-white">
                  Nächste 10 Ausführungen
                </span>
              </div>

              <select
                aria-label="Ziel-Zeitzone wählen"
                value={timeZone}
                onChange={(e) => setTimeZone(e.target.value)}
                className="min-h-[38px] rounded-xl border border-neutral-200 bg-neutral-50 px-3 py-1.5 text-xs font-semibold text-neutral-800 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-200"
              >
                {TIMEZONES.map((tz) => (
                  <option key={tz.id} value={tz.id}>
                    {tz.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Time-Travel Test Buttons (Now vs March Spring-Forward vs October Fall-Back!) */}
            <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-neutral-50 p-2 text-xs dark:bg-neutral-950/70">
              <div className="flex flex-wrap gap-1.5">
                <button
                  type="button"
                  onClick={() => {
                    setLiveNowMs(Date.now());
                    setAnchorMode("now");
                  }}
                  className={`min-h-[34px] rounded-lg px-2.5 py-1 text-xs font-semibold cursor-pointer ${
                    anchorMode === "now"
                      ? "bg-sky-600 text-white"
                      : "bg-white text-neutral-700 hover:bg-neutral-100 dark:bg-neutral-800 dark:text-neutral-300"
                  }`}
                >
                  Ab aktueller Uhrzeit
                </button>
                <button
                  type="button"
                  onClick={() => setAnchorMode("spring-dst")}
                  className={`min-h-[34px] rounded-lg px-2.5 py-1 text-xs font-semibold cursor-pointer ${
                    anchorMode === "spring-dst"
                      ? "bg-amber-600 text-white"
                      : "bg-white text-neutral-700 hover:bg-neutral-100 dark:bg-neutral-800 dark:text-neutral-300"
                  }`}
                >
                  Test: März-Sommerzeit
                </button>
                <button
                  type="button"
                  onClick={() => setAnchorMode("autumn-dst")}
                  className={`min-h-[34px] rounded-lg px-2.5 py-1 text-xs font-semibold cursor-pointer ${
                    anchorMode === "autumn-dst"
                      ? "bg-amber-600 text-white"
                      : "bg-white text-neutral-700 hover:bg-neutral-100 dark:bg-neutral-800 dark:text-neutral-300"
                  }`}
                >
                  Test: Oktober-Winterzeit
                </button>
              </div>

              <button
                type="button"
                onClick={downloadIcs}
                className="inline-flex min-h-[34px] items-center gap-1 rounded-lg border border-neutral-200 bg-white px-2.5 py-1 text-xs font-semibold text-neutral-700 hover:bg-neutral-100 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-200 cursor-pointer"
              >
                <Calendar className="h-3.5 w-3.5 text-sky-500" />
                <span>.ics Kalender</span>
              </button>
            </div>

            {/* Execution Table */}
            <div className="overflow-x-auto rounded-xl border border-neutral-200/80 dark:border-neutral-800">
              <table className="w-full text-left text-xs">
                <thead className="bg-neutral-100/80 dark:bg-neutral-800/80">
                  <tr>
                    <th className="px-3 py-2 font-semibold text-neutral-600 dark:text-neutral-300">#</th>
                    <th className="px-3 py-2 font-semibold text-neutral-600 dark:text-neutral-300">
                      Lokalzeit ({timeZone})
                    </th>
                    <th className="px-3 py-2 font-semibold text-neutral-600 dark:text-neutral-300">
                      UTC-Gegenstück
                    </th>
                    <th className="px-3 py-2 text-right font-semibold text-neutral-600 dark:text-neutral-300">
                      Abstand
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-200/80 font-mono dark:divide-neutral-800">
                  {executions.map((ex, i) => (
                    <tr key={ex.epochMs} className="hover:bg-sky-500/[0.04]">
                      <td className="px-3 py-2 text-neutral-400">{i + 1}</td>
                      <td className="px-3 py-2 font-semibold text-neutral-900 dark:text-neutral-100">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-emerald-500" />
                          <span>{ex.localFormatted}</span>
                          {ex.duplicateLocalWallClock && (
                            <span className="rounded bg-amber-500/20 px-1.5 py-0.5 font-sans text-[10px] font-bold text-amber-800 dark:text-amber-300">
                              2× durch Winterzeit!
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-neutral-500 dark:text-neutral-400">
                        {ex.utcFormatted}
                      </td>
                      <td className="px-3 py-2 text-right font-sans text-neutral-500 dark:text-neutral-400">
                        {ex.relativeText}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Right: Ready-to-Deploy Config Generator */}
          <div className="flex flex-col justify-between rounded-2xl border border-neutral-800 bg-zinc-950 p-4 text-neutral-100 lg:col-span-5">
            <div>
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-1.5">
                  <Terminal className="h-4 w-4 text-sky-400" />
                  <span className="font-heading text-sm font-bold text-white">
                    Einsatzfertiger Export
                  </span>
                </div>

                <div className="inline-flex rounded-lg bg-neutral-900 p-0.5">
                  {[
                    { id: "crontab" as const, label: "Crontab" },
                    { id: "github" as const, label: "GitHub YAML" },
                    { id: "systemd" as const, label: "systemd" },
                  ].map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setExportTarget(t.id)}
                      className={`rounded-md px-2.5 py-1 text-xs font-semibold transition-colors cursor-pointer ${
                        exportTarget === t.id
                          ? "bg-sky-600 text-white"
                          : "text-neutral-400 hover:text-white"
                      }`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>

              {exportData && (
                <div className="max-h-[330px] overflow-auto rounded-xl border border-neutral-800 bg-black/50 p-3">
                  <CodeHighlight code={exportData.code} language={exportData.lang} />
                </div>
              )}
            </div>

            <div className="mt-3 flex gap-2">
              <button
                type="button"
                onClick={async () => {
                  if (!exportData) return;
                  await navigator.clipboard?.writeText(exportData.code);
                  setCopiedExport(true);
                  setTimeout(() => setCopiedExport(false), 1800);
                }}
                className="inline-flex min-h-[42px] flex-1 items-center justify-center gap-1.5 rounded-xl bg-sky-600 px-3 py-2 text-xs font-bold text-white hover:bg-sky-700 cursor-pointer"
              >
                {copiedExport ? (
                  <>
                    <Check className="h-4 w-4" />
                    <span>Konfiguration kopiert!</span>
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4" />
                    <span>Konfiguration kopieren</span>
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={downloadIcs}
                className="inline-flex min-h-[42px] items-center justify-center gap-1.5 rounded-xl border border-neutral-700 bg-neutral-900 px-3 py-2 text-xs font-semibold text-neutral-200 hover:bg-neutral-800 cursor-pointer"
                title="Nächste 10 Termine als .ics-Kalenderdatei speichern"
              >
                <Download className="h-4 w-4" />
                <span>.ics</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
